// From the leaf module, never from `./index` — the barrel re-exports THIS file,
// so importing it back would close a require cycle through `../store`.
import { currencyFormat } from "./currency"

/**
 * Payment-term discount — the rule a supplier attaches to one payment term
 * (`de_pricing.payment_term_discount`, written by de-price-service).
 * `de-order-service` hands it to the UI on every `paymentTerms[]` entry of
 * `GET /ord/network/:id`, and as a top-level `paymentTermDiscount` for the
 * network default term (the 0-attached-terms case).
 */
export type ITermDiscount = {
  discountType : string
  discountValue: number
  bannerText?  : string | null
}

export type ITermWithDiscount = {
  id?         : string
  description?: string
  discount?   : ITermDiscount | null
}

/**
 * Discount amount taken off the ORDER total for the given rule.
 *
 * Mirrors de-order-service `logics/order/create.ts` exactly: PERCENTAGE is a
 * share of the order amount, AMOUNT is a flat sum, and the result is clamped so
 * it can never exceed the order amount (a >100% rule or an AMOUNT bigger than
 * the cart must never produce a negative total).
 *
 * An UNRECOGNISED `discountType` deducts nothing, which is also the server's
 * answer: create.ts:567-577 switches over DISCOUNT_TYPES and its `default:`
 * leaves DISCOUNT_AMOUNT at 0. Treating anything that is not "PERCENTAGE" as a
 * flat AMOUNT would have the cart, the create ledger and the buyer's receipt all
 * promise a deduction the order never receives.
 *
 * The server always recomputes this from the rule in the database; the value
 * returned here is for display and for keeping the payload in step with the UI.
 */
export const calcTermDiscount = (
  orderAmount: number,
  discount?: ITermDiscount | null,
): number => {
  const base  = Number(orderAmount) || 0
  const value = Number(discount?.discountValue) || 0
  if (!discount || !value || base <= 0) return 0

  let amount = 0
  if (discount.discountType === "PERCENTAGE") amount = (base * value) / 100
  else if (discount.discountType === "AMOUNT") amount = value
  else return 0

  if (!(amount > 0)) return 0

  return Math.min(amount, base)
}

/**
 * Banner text for one payment term. The supplier's own `bannerText` wins;
 * otherwise the sentence is generated from the rule.
 * Returns null when the term carries no live rule.
 */
export const termDiscountLabel = (
  term: ITermWithDiscount | null | undefined,
  t: (key: string, options?: any) => string,
): string | null => {
  const discount = term?.discount
  if (!discount || !discount.discountValue) return null
  if (discount.bannerText) return discount.bannerText

  const value = discount.discountType === "PERCENTAGE"
    ? `${discount.discountValue}%`
    : currencyFormat(discount.discountValue)

  return t("order:buyerSales.termDiscountBanner", { term: term?.description || "", value })
}

/**
 * Rule that applies to the term an order will actually be sent with.
 *
 * `network` is a `GET /ord/network/:id` response. When no term was picked, the
 * single attached term (or, with none attached, the network default exposed as
 * `paymentTermDiscount`) is the one the backend will use.
 */
export const findTermDiscount = (
  network: any,
  paymentTermId?: string | null,
): ITermDiscount | null => {
  const terms: ITermWithDiscount[] = network?.paymentTerms ?? []

  if (paymentTermId) {
    const term = terms.find((x) => x?.id === paymentTermId)
    if (term) return term.discount ?? null
    // Not an attached term — the only other id the payload can carry is the
    // network default, whose rule arrives as a top-level `paymentTermDiscount`.
    return paymentTermId === network?.paymentTermId ? (network?.paymentTermDiscount ?? null) : null
  }

  if (terms.length === 1) return terms[0]?.discount ?? null
  if (terms.length === 0) return network?.paymentTermDiscount ?? null

  // 2+ attached terms and nothing picked yet — nothing is applied.
  return null
}

/**
 * Short suffix that names the rule, e.g. " (10%)" or " (24,580₮)".
 * Used on the ledger/breakdown rows next to the discount label.
 */
export const termDiscountSuffix = (discount?: ITermDiscount | null): string => {
  if (!discount || !discount.discountValue) return ""
  return discount.discountType === "PERCENTAGE"
    ? ` (${discount.discountValue}%)`
    : ` (${currencyFormat(discount.discountValue)})`
}
